import type {
  HandoffContext,
  HandoffContextBlock,
  HandoffContextStatus,
} from 'v2/types/api/handoff-context'

// Only HS writes the policy this setup configures; hide while profile/rollout is unavailable.
export const canConfigureHandoffContext = (enabled: boolean | undefined, flowType?: string) =>
  enabled === true && (flowType === 'incoming_call' || flowType === 'omni')

export const UPLOAD_PATH = 'v1/handoff-context'
export const SIP_HEADER = 'X-PH-HandoffID'
export const NOT_RECEIVED_GRACE_MS = 45_000

/** "collected_fields" -> "Collected fields". */
export const fieldLabel = (field: string): string =>
  field.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())

/** The one shared regional upload URL: same for every Flow of the organization. */
export const uploadUrl = (resolvedApiUrl: string): string =>
  `${resolvedApiUrl.replace(/\/?$/, '/')}${UPLOAD_PATH}`

export const EXAMPLE_REQUEST = `{
  "schema_version": "1.0",
  "handoff_id": "7f92a136cc204830a0459dc12c4a519b",
  "revision": 1,
  "original_caller": "+14155550123",
  "transfer_reason": "Address change needs a human",
  "summary": "Wants to change the delivery address for order 4417.",
  "collected_fields": { "order_id": "4417", "new_city": "Austin" },
  "transcript": [
    { "speaker": "customer", "text": "I need to change my delivery address" },
    { "speaker": "agent", "text": "Let me get a human specialist for you" }
  ]
}`

export const DTMF_EXAMPLE = `# Python
digits = str(int(handoff_id, 16)).zfill(39)

// JavaScript
const digits = BigInt("0x" + handoffId).toString().padStart(39, "0")`

/** Plain-text setup steps for the developer, in the same words as the dialog. */
export const integrationInstructions = (url: string): string =>
  [
    'Connect your AI agent: Plivo CX transfer context',
    '',
    '1. Send the transfer context',
    `   POST ${url}`,
    '   Authorization: Bearer <API key>',
    '   Use a new handoff ID for every transfer: a UUID written as 32 lowercase hex characters.',
    ...EXAMPLE_REQUEST.split('\n').map((line) => `   ${line}`),
    '   202: stored. 200: already stored. Resend the same request if you are unsure it arrived.',
    '   To update it, send every field again with revision + 1 (an older revision gets 409).',
    '   original_caller is the customer’s number in E.164 format; leave it out for withheld callers.',
    '',
    '2. Transfer the call',
    `   Transfer it to this Flow's phone number with the SIP header ${SIP_HEADER}: <handoff ID>.`,
    '   The call never waits for the transfer context; context that arrives later appears on its own.',
    '',
    'Optional: wait for a human specialist to answer',
    `   GET ${url}/<handoff ID>/status with the same header until it returns "accepted", then leave the call.`,
    '',
    'Calls from outside Plivo',
    "   If your AI agent's calls don't run on Plivo, the transfer arrives as a regular phone call and the SIP header is lost.",
    '   1. In this Flow, add a Collect response step before Assign to human and turn on Receive AI transfer reference.',
    '   2. Keep your AI agent on the call. As soon as it connects, play the handoff ID as 39 keypad tones:',
    '   BigInt("0x" + handoffId).toString().padStart(39, "0")',
  ].join('\n')

/** Poll only while a fresh pending policy could still receive context; afterwards rely on events. */
export const pollWhilePending = (
  data: HandoffContext | undefined,
  now: number = Date.now(),
): boolean =>
  !!data &&
  data.status === 'pending' &&
  !!data.pending_since &&
  now - Date.parse(data.pending_since) <= NOT_RECEIVED_GRACE_MS

export type PanelState =
  | 'hidden'
  | 'pending'
  | 'not_received'
  | 'available'
  | 'unavailable'
  | 'error'

/** What the agent panel shows for a read result; the grace timer is a UI default, never a call wait. */
export const panelState = (
  data: HandoffContext | undefined,
  isError: boolean,
  now: number = Date.now(),
  httpStatus?: number,
): PanelState => {
  if (isError) {
    // Authorization/deletion wins over cached data. Only transient failures retain a prior view.
    if ([403, 404].includes(httpStatus ?? 0)) return 'hidden'
    if (!data || (httpStatus !== undefined && httpStatus < 500)) {
      return httpStatus === 503 ? 'hidden' : 'error'
    }
  }
  if (!data) return 'hidden'
  const map: Record<HandoffContextStatus, PanelState> = {
    not_configured: 'hidden',
    available: 'available',
    unavailable: 'unavailable',
    pending: pollWhilePending(data, now) ? 'pending' : 'not_received',
  }
  return map[data.status]
}

/** The wire has no turn IDs. Content + duplicate occurrence stays stable when other turns move. */
export const transcriptRows = (turns: NonNullable<HandoffContextBlock['transcript']>) => {
  const occurrences = new Map<string, number>()
  return turns.map((turn) => {
    const identity = JSON.stringify([turn.speaker, turn.text, turn.offset_ms ?? null])
    const occurrence = occurrences.get(identity) || 0
    occurrences.set(identity, occurrence + 1)
    return { turn, key: `${identity}:${occurrence}` }
  })
}
